import type {FC} from 'react';
import {useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import type {NzbHealthDebugData} from '@repo/shared/api/website_api';
import {errorAsString} from '@repo/shared/lib/type_utils';

import {Button} from '@repo/shared-web/components/core/button';
import {Input} from '@repo/shared-web/components/core/input';
import {useStringQueryString} from '@repo/shared-web/lib/use_query_string';

import {apiCall} from '@repo/website-frontend/lib/api';
import {formatReleaseDate} from '@repo/website-frontend/lib/date';

const PAGE_SIZE = 50;

export const NzbHealthDebugPage: FC = () => {
  const [requestedGuid, setRequestedGuid, requestedGuidRef] = useStringQueryString('guid', '');
  const [searchGuid, setSearchGuid] = useState(requestedGuid);
  const [refreshKey, setRefreshKey] = useState(0);
  const [data, setData] = useState<NzbHealthDebugData | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    setSearchGuid(requestedGuid);
  }, [requestedGuid]);

  useEffect(() => {
    const guid = requestedGuid.trim();
    if (!guid) {
      setData(undefined);
      setError(undefined);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    let active = true;
    setLoading(true);
    setLoadingMore(false);
    setData(undefined);
    setError(undefined);
    apiCall('POST /get-nzb-health-debug', {guid, paginationToken: undefined, limit: PAGE_SIZE})
      .then(result => {
        if (active) {
          setData(result);
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshKey, requestedGuid]);

  const handleInspectClick = useCallback(() => {
    setRequestedGuid(searchGuid.trim());
    setRefreshKey(current => current + 1);
  }, [searchGuid, setRequestedGuid]);

  const handleRefreshClick = useCallback(() => {
    setRefreshKey(current => current + 1);
  }, []);

  const handleLoadMoreClick = useCallback(async () => {
    if (!requestedGuid || !data?.paginationToken) {
      return;
    }
    setLoadingMore(true);
    setError(undefined);
    try {
      const nextPage = await apiCall('POST /get-nzb-health-debug', {
        guid: requestedGuid,
        limit: PAGE_SIZE,
        paginationToken: data.paginationToken,
      });
      setData(current =>
        requestedGuidRef.current === requestedGuid && current?.guid === requestedGuid
          ? {
              ...current,
              checks: [...current.checks, ...nextPage.checks],
              paginationToken: nextPage.paginationToken,
            }
          : current,
      );
    } catch (err: unknown) {
      if (requestedGuidRef.current === requestedGuid) {
        setError(errorAsString(err));
      }
    } finally {
      setLoadingMore(false);
    }
  }, [data?.paginationToken, requestedGuid, requestedGuidRef]);

  const handleRunCheckClick = useCallback(async () => {
    await apiCall('POST /check-nzb', {id: requestedGuid});
    setRefreshKey(current => current + 1);
  }, [requestedGuid]);

  const latestCheck = data?.lastCheck;
  const markerLabel = !data?.manifest.exists
    ? 'No cached manifest'
    : data.manifest.validationMarker === 'v1'
      ? 'Validated marker v1'
      : data.manifest.validationMarker
        ? `Unknown marker: ${data.manifest.validationMarker}`
        : 'Cached object without validation marker';

  return (
    <Wrapper>
      <PageHeader>
        <h1>NZB health debug</h1>
        <p>
          Inspect cached manifest metadata and the saved NNTP availability checks for a release.
        </p>
      </PageHeader>

      <InfoGrid>
        <InfoCard>
          <h2>Manifest integrity</h2>
          <p>
            Automatic XML validation was introduced with the recovery rollout on 11 Oct 2026.
            Downloaded manifests are validated before caching. Existing objects are validated when
            the cache path encounters them; 1% of already marked objects are sampled
            deterministically when accessed.
          </p>
          <p>
            This is cache work, not a full periodic scan of every release. The S3 marker records
            validation version but not validation time.
          </p>
        </InfoCard>
        <InfoCard>
          <h2>Usenet availability</h2>
          <p>
            NNTP checks run when requested. Each check samples up to 10 random article segments and
            tests their headers. Results are saved per release; a passing sample does not confirm
            every segment is still downloadable.
          </p>
        </InfoCard>
      </InfoGrid>

      <Panel>
        <h2>Inspect a release</h2>
        <LookupRow>
          <Input<string>
            aria-label="NZB GUID"
            placeholder="Paste an NZB GUID"
            value={searchGuid}
            syncState={setSearchGuid}
          />
          <Button onClick={handleInspectClick} disabled={searchGuid.trim().length === 0}>
            Inspect
          </Button>
        </LookupRow>
      </Panel>

      {loading ? <Message>Loading health details…</Message> : <></>}
      {error ? <ErrorMessage>{error}</ErrorMessage> : <></>}

      {data ? (
        <>
          <Panel>
            <ResultHeader>
              <div>
                <h2>{data.release?.title ?? 'Release record not found'}</h2>
                <Guid>{data.guid}</Guid>
              </div>
              <Button onClick={handleRefreshClick}>Refresh</Button>
            </ResultHeader>
            {data.release ? (
              <ReleaseDetails>
                <Detail>
                  <DetailLabel>Release date</DetailLabel>
                  <DetailValue>{formatReleaseDate(data.release.pubTs)}</DetailValue>
                </Detail>
                <Detail>
                  <DetailLabel>Database cache flag</DetailLabel>
                  <DetailValue>{data.release.nzb}</DetailValue>
                </Detail>
                <Detail>
                  <DetailLabel>NZB object</DetailLabel>
                  <DetailValue>{markerLabel}</DetailValue>
                </Detail>
                <Detail>
                  <DetailLabel>Stored object size</DetailLabel>
                  <DetailValue>
                    {data.manifest.contentLength === undefined
                      ? '—'
                      : `${data.manifest.contentLength.toLocaleString()} bytes (compressed)`}
                  </DetailValue>
                </Detail>
                <Detail>
                  <DetailLabel>S3 last modified</DetailLabel>
                  <DetailValue>
                    {data.manifest.lastModified === undefined
                      ? '—'
                      : formatTimestamp(data.manifest.lastModified)}
                  </DetailValue>
                </Detail>
              </ReleaseDetails>
            ) : (
              <ReleaseDetails>
                <Detail>
                  <DetailLabel>NZB object</DetailLabel>
                  <DetailValue>{markerLabel}</DetailValue>
                </Detail>
                <Detail>
                  <DetailLabel>S3 last modified</DetailLabel>
                  <DetailValue>
                    {data.manifest.lastModified === undefined
                      ? '—'
                      : formatTimestamp(data.manifest.lastModified)}
                  </DetailValue>
                </Detail>
              </ReleaseDetails>
            )}
            {data.manifest.exists ? (
              <Note>S3 last modified is the object write time, not the XML validation time.</Note>
            ) : (
              <Note>No cached NZB object was found for this GUID.</Note>
            )}
          </Panel>

          <Panel>
            <ResultHeader>
              <div>
                <h2>NNTP check history</h2>
                <p>
                  {latestCheck
                    ? `Latest check: ${latestCheck.success} articles present, ${latestCheck.failure} failed; sampled ${latestCheck.checked} of ${latestCheck.total}.`
                    : 'No saved NNTP check for this release.'}
                </p>
                {latestCheck ? <Note>Last checked {formatTimestamp(latestCheck.ts)}.</Note> : <></>}
              </div>
              <Button onClickAsync={handleRunCheckClick}>Run sampled NNTP check</Button>
            </ResultHeader>
            {data.checks.length > 0 ? (
              <HistoryTable>
                <HistoryHeader>
                  <span>Checked</span>
                  <span>Articles present / failed</span>
                  <span>Sampled / total</span>
                </HistoryHeader>
                {data.checks.map(check => (
                  <HistoryRow key={check.id}>
                    <span>{formatTimestamp(check.ts)}</span>
                    <span>
                      {check.success} / {check.failure}
                    </span>
                    <span>
                      {check.checked} / {check.total}
                    </span>
                  </HistoryRow>
                ))}
              </HistoryTable>
            ) : (
              <Message>No check history is stored for this GUID.</Message>
            )}
            {data.paginationToken ? (
              <Button onClickAsync={handleLoadMoreClick} loading={loadingMore}>
                Load older checks
              </Button>
            ) : (
              <></>
            )}
          </Panel>
        </>
      ) : (
        <></>
      )}
    </Wrapper>
  );
};

NzbHealthDebugPage.displayName = 'NzbHealthDebugPage';

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  max-width: 1200px;
  width: 100%;
  align-self: center;
`;

const PageHeader = styled.header`
  h1 {
    margin: 0 0 8px;
  }
  p {
    margin: 0;
    color: #ffffffaa;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
  gap: 16px;
`;

const InfoCard = styled.section`
  background: #1f242f;
  border: 1px solid #ffffff1a;
  border-radius: 8px;
  padding: 16px;
  h2 {
    margin: 0 0 10px;
    font-size: 18px;
  }
  p {
    margin: 8px 0 0;
    color: #ffffffbb;
    line-height: 1.5;
  }
`;

const Panel = styled.section`
  background: #1f242f;
  border: 1px solid #ffffff1a;
  border-radius: 8px;
  padding: 16px;
  h2 {
    margin: 0 0 12px;
    font-size: 18px;
  }
  p {
    margin: 0;
    color: #ffffffbb;
  }
`;

const LookupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  > input {
    flex: 1;
    min-width: 0;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  h2 {
    margin-bottom: 6px;
  }
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const Guid = styled.div`
  color: #ffffff88;
  font-family: monospace;
  overflow-wrap: anywhere;
`;

const ReleaseDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: 14px;
`;

const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  color: #ffffff88;
  font-size: 13px;
`;

const DetailValue = styled.span`
  overflow-wrap: anywhere;
`;

const Note = styled.div`
  margin-top: 12px;
  color: #ffffff88;
  font-size: 13px;
`;

const HistoryTable = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
`;

const HistoryHeader = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 12px;
  padding: 8px 0;
  color: #ffffff88;
  border-bottom: 1px solid #ffffff22;
  @media (max-width: 640px) {
    grid-template-columns: 1.2fr 1fr;
    span:last-child {
      display: none;
    }
  }
`;

const HistoryRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #ffffff12;
  @media (max-width: 640px) {
    grid-template-columns: 1.2fr 1fr;
    span:last-child {
      display: none;
    }
  }
`;

const Message = styled.div`
  color: #ffffff88;
  padding: 8px 0;
`;

const ErrorMessage = styled(Message)`
  color: #ff8585;
`;
